import { networkFromServiceCode } from "@/lib/network-service-codes"
import {
  initialsFromName,
  type OutputAccount,
} from "@/shadcn/components/NewAutomation/OutputTile"

/**
 * The bridge from a backend social record to the `OutputAccount` the new-flow
 * destination tile draws.
 *
 * Two surfaces need the same account to look the same: `SocialButton`, which
 * draws one tile inside a legacy list, and `DestinationsStep`, which draws the
 * whole grid from an array. Without this they would each derive the `@` handle
 * prefix, the initials, the avatar fallback chain and the subtype label from the
 * same fields, and drift the first time one of them was corrected.
 *
 * Naming, not policy: nothing here decides whether an account is selectable or
 * badged as a retweeter — the caller knows its own context and passes those in.
 */

/** The fields of a social record this file reads. Legacy data, so all optional. */
export type SocialRecord = {
  id?: number | string
  name?: string
  psID?: number | string
  gsID?: number | string
  subType?: number | string | null
  /** Profile image; `src` is what some list payloads call it. */
  icon?: string
  src?: string
  activeFlag?: number | string
  /** Set when the SYSTEM switched the account off — it needs re-authorising. */
  sysActionFlag?: number | string
}

/**
 * `window.DlvrConstants`, as callers hold it (`app.config.constants`). Typed as
 * a bag because the generated constant file is not a module.
 */
type Constants = Record<string, number>

export type OutputAccountOptions = {
  constants: Constants
  /** Overrides the derived display name. */
  label?: string
  /** Overrides the derived subtype line — e.g. the queue's "62% full". */
  subtype?: string
  selected?: boolean
  inactive?: boolean
  /** Draws the retweet badge in place of the network's. */
  retweet?: boolean
  /**
   * Makes the tile unselectable and supplies its tooltip. Truthy means disabled,
   * so a caller can pass the reason it computed and nothing else.
   */
  disabledReason?: string
}

function toInt(value: number | string | null | undefined): number {
  return parseInt(String(value ?? ""), 10)
}

/**
 * The account's display name: X accounts wear the `@` their handle is stored
 * without, everything else reads as the backend spells it.
 */
export function outputAccountLabel(
  social: SocialRecord,
  constants: Constants
): string {
  const name = String(social.name ?? "")
  return toInt(social.psID) === constants.POST_SVC_TWITTER ? `@${name}` : name
}

/**
 * The tile's third line, for the two services whose accounts come in kinds a
 * user has to tell apart — a LinkedIn person vs company page, a Facebook
 * profile vs group vs page. "" for everything else, which renders no line.
 */
export function outputAccountSubtype(
  social: SocialRecord,
  constants: Constants
): string {
  const subType = toInt(social.subType)

  if (!subType || subType <= 0) return ""

  const service = toInt(social.psID)

  if (service === constants.POST_SVC_LINKEDIN) {
    if (subType === constants.LI_PERSON) return "Person"
    if (subType === constants.LI_COMPANY) return "Company"
    return "Subtype"
  }

  if (service === constants.POST_SVC_FACEBOOK) {
    if (subType === constants.FB_PROFILE) return "Profile"
    if (subType === constants.FB_GROUP) return "Group"
    if (subType === constants.FB_PAGE) return "Page"
    return "Subtype"
  }

  return ""
}

/** Whether the account is switched off, by the user or by the system. */
export function outputAccountInactive(social: SocialRecord): boolean {
  return toInt(social.activeFlag) === 0
}

/**
 * Whether an account needs re-authorising, as opposed to merely being switched
 * off. The system sets `sysActionFlag` when it gives up on an account's
 * credentials, and only a reconnect clears it — so the two states want different
 * treatments and different actions, which is why this is separate from
 * `outputAccountInactive`.
 */
export function outputAccountDisconnected(social: SocialRecord): boolean {
  return toInt(social.sysActionFlag) === 1
}

/**
 * The brand mark for an account.
 *
 * A generic-service record — a WordPress site posting through the generic
 * connector — carries its code in `gsID` instead of `psID`, and the two spaces
 * collide: GENERIC_SVC_WORDPRESS and POST_SVC_TWITTER are both 1. So the
 * fallback has to say which space it is in, or a WordPress destination wears the
 * X mark. `SocialAvatar` has always passed 'generic' here; this is the same hop.
 *
 * `psID` is tested for a real value rather than for null: `?? ` would let a 0
 * through as a post code, and 0 is not one.
 */
export function outputAccountNetwork(social: SocialRecord) {
  if (social.psID) return networkFromServiceCode(social.psID)
  if (social.gsID) return networkFromServiceCode(social.gsID, "generic")

  return null
}

export function outputAccountFromSocial(
  social: SocialRecord,
  options: OutputAccountOptions
): OutputAccount {
  const { constants, retweet, disabledReason } = options

  return {
    id: String(social.id ?? ""),
    name: options.label ?? outputAccountLabel(social, constants),
    network: outputAccountNetwork(social),
    initials: initialsFromName(social.name),
    avatarUrl: social.icon || social.src || undefined,
    subtype:
      (options.subtype ?? outputAccountSubtype(social, constants)) || undefined,
    selected: Boolean(options.selected),
    inactive: Boolean(options.inactive),
    /*
     * Read from the record rather than taken from the caller, which `inactive`
     * has to be: an output can be switched off while its social is fine, so only
     * the caller knows which of the two it means. `sysActionFlag` has no such
     * ambiguity — it exists only on the social, and it means one thing — so
     * deriving it here is what stops each new adopter of the tile having to
     * remember to pass it.
     */
    disconnected: outputAccountDisconnected(social),
    badgeIcon: retweet ? "retweet" : undefined,
    badgeLabel: retweet ? "Retweet" : undefined,
    disabled: Boolean(disabledReason),
    disabledReason: disabledReason || undefined,
  }
}
